import { useEffect } from 'react';
import { getSessionOnce, clearSessionCache } from './ory';
import { redirectToLogin } from './RedirectToLoginPage';
import { Logger } from 'src/utils/Logger';

/**
 * Hook that monitors the session expiration and preemptively logs out the user
 * before the session expires to prevent 401 errors.
 *
 * @param bufferSeconds - Number of seconds before expiration to trigger logout (default: 10)
 */
export const useSessionExpirationMonitor = (bufferSeconds: number = 10) => {
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const setupExpirationTimer = async () => {
      try {
        const session = await getSessionOnce();

        if (!session?.expires_at) {
          Logger.warn('Session does not have an expires_at field');
          return;
        }

        const expiresAt = new Date(session.expires_at);
        const now = new Date();
        const timeUntilExpiration = expiresAt.getTime() - now.getTime();

        const timeUntilLogout = timeUntilExpiration - bufferSeconds * 1000;

        if (timeUntilLogout <= 0) {
          Logger.info(
            'Session has expired or is about to expire, redirecting to login immediately',
          );
          clearSessionCache();
          redirectToLogin();
          return;
        }

        Logger.info(
          `Session will expire at ${expiresAt.toISOString()}. Scheduling logout in ${Math.round(timeUntilLogout / 1000)} seconds`,
        );

        timeoutId = setTimeout(() => {
          Logger.info('Session is about to expire, redirecting to login preemptively');
          clearSessionCache();
          redirectToLogin();
        }, timeUntilLogout);
      } catch (error) {
        // Don't log 401 errors as they indicate an expired/invalid session, which is expected
        // The auth check will handle redirecting to login
        const isUnauthorized =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error.response as { status: number })?.status === 401;

        const isTimeout =
          error &&
          typeof error === 'object' &&
          'code' in error &&
          (error as { code: string }).code === 'ECONNABORTED';

        if (!isUnauthorized && !isTimeout) {
          Logger.error('Failed to setup session expiration timer', error);
        }
      }
    };

    setupExpirationTimer();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [bufferSeconds]);
};
